import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Custom hook to get accountId that handles multi-tab scenarios
 * 
 * KEY FEATURES:
 * 1. Always prioritizes Redux state (tab-specific) over localStorage (shared)
 * 2. Each tab maintains its own accountId independently
 * 3. Changes in one tab don't automatically affect other tabs
 * 
 * This solves the issue where opening 2 tabs in the same browser causes
 * accountId conflicts because localStorage is shared across tabs.
 * 
 * @returns {Object} - { accountId, selectedAccount, isLoading }
 */
const useAccountId = () => {
  const selectedAccount = useSelector((state) => state.common.selectedAccount);

  // Get accountId from Redux state (tab-specific)
  // Redux state is tab-specific, so each tab has its own selectedAccount
  // 
  // IMPORTANT: This ensures complete tab isolation:
  // - Tab 1 selects Account A → Redux Tab 1 = Account A, localStorage = "A"
  // - Tab 2 selects Account B → Redux Tab 2 = Account B, localStorage = "B"  
  // - Tab 1 changes to Account C → Redux Tab 1 = Account C, localStorage = "C"
  // - Tab 2 reads accountId → Returns "B" from Redux Tab 2 (NOT "C" from localStorage)
  // 
  // localStorage changes from other tabs are IGNORED once Redux state exists.
  const accountId = useMemo(() => {
    // ALWAYS read from Redux first (tab-specific, isolated per tab)
    if (selectedAccount && selectedAccount._id) {
      return selectedAccount._id;
    }
    
    // Fallback to sessionStorage ONLY if Redux state is empty (initial page load)
    // Once Redux has data, sessionStorage changes from other tabs won't affect this tab
    try {
      return sessionStorage.getItem("accountId");
    } catch (error) {
      console.warn('Error reading accountId from sessionStorage:', error);
      return null;
    }
  }, [selectedAccount]);

  return {
    accountId,
    isLoading: false,
    selectedAccount // Also return full account object for components that need it
  };
};

export default useAccountId;
